import {
  saveCropTour,
  uploadCropTourFile,
} from '../../../apis/premier-plus-api';
import {handleApiError} from '../../../shared/actions';

// 2104 West Park Court, Champaign, IL 61821. Used when the browser will not give
// us a location on the initial load of the Yield Estimator.
const YIELD_ESTIMATOR_FALLBACK_LATITUDE = 40.11795550708601;

const YIELD_ESTIMATOR_FALLBACK_LONGITUDE = -88.2829670124286;

const getGeolocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
    } 
    else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(new Error('User denied the request for Geolocation'));
              break;
            case error.POSITION_UNAVAILABLE:
              reject(new Error('Position update is unavailable'));
              break;
            case error.TIMEOUT:
              reject(new Error('The request to get user location timed out'));
              break;
            default:
              reject(new Error('An unknown error occurred'));
          }
        },
        {enableHighAccuracy: true, timeout: 5000, maximumAge: 0}
      );
    }
  });
};

export const initYieldEstimatorInterface = () => (async (dispatch, getState) => {
  await dispatch({
    type: 'INIT_YIELD_ESTIMATOR_INTERFACE',
  });
  dispatch(findMe(false, true));
});

export const findMe = (resetLocation = false, useFallbackLocation = false) => (async (dispatch, getState) => {
  try {
    if (resetLocation === true) {
      dispatch({
        type: 'SET_YIELD_ESTIMATOR_GEOLOCATION',
        latitude: 0,
        longitude: 0,
      });
    }
    const {latitude, longitude} = await getGeolocation();
    dispatch({
      type: 'SET_YIELD_ESTIMATOR_GEOLOCATION',
      latitude,
      longitude,
    });
  }
  catch (error) {
    alert('Your browser is denying access to your location. Make sure you allowed access to your location when prompted to by your browser. If you did not allow access when prompted, then you will have to reset the location permission in your browser.');
    if (useFallbackLocation === true) {
      dispatch({
        type: 'SET_YIELD_ESTIMATOR_GEOLOCATION',
        latitude: YIELD_ESTIMATOR_FALLBACK_LATITUDE,
        longitude: YIELD_ESTIMATOR_FALLBACK_LONGITUDE,
      });
    }
  }
});

export const updateYieldEstimatorGeolocation = (latitude, longitude) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_GEOLOCATION',
    latitude,
    longitude,
  });
});

export const setYieldEstimatorPlantPopulation = (plantPopulation) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_PLANT_POPULATION',
    plantPopulation,
  });
});

export const setYieldEstimatorKernelsPerBushel = (kernelsPerBushel) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_KERNELS_PER_BUSHEL',
    kernelsPerBushel,
  });
});

export const setYieldEstimatorEarsRows = (index, rows) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_EARS_ROWS',
    index,
    rows,
  });
});

export const setYieldEstimatorEarsLength = (index, length) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_EARS_LENGTH',
    index,
    length,
  });
});

export const setYieldEstimatorFile = (file) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_FILE_TO_UPLOAD',
    file,
  });
});

export const setYieldEstimatorGrowerIdOverride = (growerId) => (async (dispatch, getState) => {
  dispatch({
    type: 'SET_YIELD_ESTIMATOR_GROWER_ID_OVERRIDE',
    growerId,
  });
});

export const calculateYield = () => (async (dispatch, getState) => {
  const {
    yieldEstimatorLatitude,
    yieldEstimatorLongitude,
    plantPopulation,
    kernelsPerBushel,
    ears,
    yieldEstimatorFileToUpload,
    yieldEstimatorGrowerIdOverride,
  } = getState().toolBox;

  if (yieldEstimatorLatitude === 0 || yieldEstimatorLongitude === 0) {
    alert('Please allow access to your location.');
    return;
  }

  if (plantPopulation <= 0 || kernelsPerBushel <= 0) {
    alert('Plant population and kernels per bushel must be greater than zero.');
    return;
  }

  // Step 1: Ears per acre (using plant population, assuming 1 ear per plant)
  const earsPerAcre = plantPopulation;

  // Step 2: Calculate kernels per ear for each ear (rows * length)
  const validEars = ears.filter(ear => ear.rows > 0 && ear.length > 0);

  if (validEars.length === 0) {
    alert('You must enter at least one ear row and length.');
    return;
  }

  // Step 3: Average kernels per ear across valid ears
  const totalKernels = validEars.reduce((sum, ear) => sum + (ear.rows * ear.length), 0);

  const avgKernelsPerEar = totalKernels / validEars.length;

  // Step 4: Estimate calculatedYield: (ears per acre * avg kernels per ear) / kernels per bushel
  const calculatedYield = (earsPerAcre * avgKernelsPerEar) / kernelsPerBushel;

  dispatch({
    type: 'SET_YIELD_ESTIMATOR_CALCULATED_YIELD',
    calculatedYield: calculatedYield.toFixed(2),
  });

  var growerId = getState().auth.authorization.growerId;
  const emailAddress = getState().auth.authorization.emailAddress;

  if (getState().auth.authorization.isYieldEstimatorUser === true) {
    growerId = yieldEstimatorGrowerIdOverride;
  }
  
  const token = getState().auth.authorization.token;

  var response = null;
  try {
    response = await saveCropTour(yieldEstimatorLatitude, yieldEstimatorLongitude, growerId, emailAddress, plantPopulation, kernelsPerBushel, ears, calculatedYield, token);
  }
  catch (error) {
    dispatch(handleApiError(error));
  }

  if (yieldEstimatorFileToUpload !== null && response?.body?.cropTourId !== '') {
    try {
      await uploadCropTourFile(yieldEstimatorFileToUpload, response.body.cropTourId, getState().auth.authorization.token);
      dispatch(setYieldEstimatorFile(null));
    }
    catch (error) {
      //dispatch(handleApiError(error));
    }
  }
});
